// ---------------------------------------------------------------------------
// RouteErrorBoundary — one broken page doesn't take the shell with it (AL-2004)
// ---------------------------------------------------------------------------
// Sits around the v11 route table, inside V11Layout, so the sidebar and the
// topbar survive a render error and the user can navigate away.
//
// Sentry's boundary has no resetKeys. Keying it on the location would remount
// every page on every navigation (Lens → Lens of another post, for one), so
// the fallback resets the boundary itself on the next navigation. It watches
// location.key, not the pathname: clicking the broken page's own sidebar link,
// or changing a filter in the topbar, is the user asking for it again too.
//
// The fallback is passed as a module-level component, never an inline arrow:
// Sentry renders it with createElement, so a new function per render is a new
// component type. The layout re-renders on every navigation, which remounted
// the fallback with the new location and it never saw the change.
// ---------------------------------------------------------------------------

import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Sentry } from '@/config/sentry';
import { ErrorFallback } from './ErrorFallback';
import { reloadForStaleChunk } from './chunkReload';
import { useReloadPending } from './useReloadPending';
import { isStaleBuildError } from './isStaleBuildError';

interface RouteFallbackProps {
  readonly error: unknown;
  readonly resetError: () => void;
}

function RouteFallback({ error, resetError }: RouteFallbackProps) {
  const { key } = useLocation();
  const [failedAt] = useState(key);
  // Decided when the error is caught, not on every render (see isStaleBuildError).
  const [staleBuild] = useState(() => isStaleBuildError(error));
  const [reloading, refresh] = useReloadPending();

  useEffect(() => {
    if (key !== failedAt) resetError();
  }, [key, failedAt, resetError]);

  // React.lazy caches the rejected import, so a stale chunk can't be retried
  // in place. vite:preloadError usually reloads first; this covers the imports
  // Vite doesn't wrap, under the same guard against loops.
  useEffect(() => {
    if (staleBuild && reloadForStaleChunk()) refresh();
  }, [staleBuild, refresh]);

  // An empty fragment, not null: Sentry's fallback type must return an element.
  if (reloading) return <></>;

  return <ErrorFallback variant="page" staleBuild={staleBuild} onRetry={resetError} />;
}

export function RouteErrorBoundary({ children }: { readonly children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      beforeCapture={scope => scope.setTag('error_boundary', 'route')}
      fallback={RouteFallback}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
